import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'react-qr-code';
import { formatDate } from '@/shared/utils/format';
import { referralsApi } from '../api/referralsApi';
import { referralKeys } from '../queryKeys';
import { ToothChart } from '../components';
import styles from './PrintReferralPage.module.css';

/**
 * /referrals/:id/print?copy=office|patient&locationId=… — the paper versions of a sent slip,
 * rendered without the app chrome and sized for Letter.
 *
 * Office copy: the full slip plus a QR code and URL to the secure link, so an office that was
 * handed a sheet of paper can still open the digital referral, X-rays included, with no account.
 * Patient copy: where to go, whom to call, and what it is for, with a QR to the patient's page.
 */
export function PrintReferralPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const copy = params.get('copy') === 'patient' ? 'patient' : 'office';
  const locationId = params.get('locationId') ?? '';
  const referral = useQuery({
    queryKey: referralKeys.detail(locationId, id),
    queryFn: () => referralsApi.get(locationId, id),
    enabled: Boolean(id && locationId),
  });
  const template = useQuery({
    queryKey: referralKeys.template(locationId, null),
    queryFn: () => referralsApi.getTemplate(locationId, null),
    enabled: Boolean(locationId),
  });

  useEffect(() => {
    if (referral.data && template.data) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [referral.data, template.data]);

  if (referral.isError) return <div className={styles.page}>This referral could not be loaded.</div>;
  if (!referral.data || !template.data) return <div className={styles.page}>Preparing the printable slip…</div>;

  const r = referral.data;
  const t = template.data;
  // Which office's copy: ?recipient=<id>, else the chosen or only one, else the first.
  const recOpt = r.recipients.find((x) => x.id === params.get('recipient')) ?? r.recipients.find((x) => x.isChosen) ?? r.recipients[0] ?? null;
  const rec = recOpt?.practice ?? r.recipient;
  const officeLink = recOpt?.linkPreview ?? r.recipientLinkPreview;
  const preferredName = recOpt?.preferredProviderName ?? r.preferredProviderName;
  const openOptions = r.recipients.filter((x) => x.status !== 'cancelled' && x.status !== 'superseded' && x.status !== 'pending');
  const multi = openOptions.length > 1 && !r.chosenRecipientId;
  const recAddress = rec ? [rec.address, [rec.city, rec.state].filter(Boolean).join(', '), rec.zip].filter(Boolean).join(' · ') : '';

  if (copy === 'patient') {
    return (
      <div className={styles.page}>
        <div className={styles.toolbar}>
          <button type="button" onClick={() => window.print()} className={styles.printBtn}>
            Print
          </button>
        </div>
        <div className={styles.sheet}>
          <header className={styles.head}>
            <div>
              <div className={styles.practice}>{r.senderPracticeName}</div>
              {r.senderPhone && <div className={styles.muted}>{r.senderPhone}</div>}
            </div>
            <div className={styles.docTitle}>Your referral</div>
          </header>
          <p className={styles.lead}>
            {r.patientFirstName}, {r.referringDoctorName ? `${r.referringDoctorName} at ` : ''}
            {r.senderPracticeName} has referred you to <strong>{multi ? 'a specialist' : (rec?.name ?? 'a specialist')}</strong>
            {r.reasons.length ? ` for ${r.reasons.join(', ').toLowerCase()}` : ''}
            {r.vertical === 'dental' && r.teethLabel ? ` (${r.teethLabel})` : r.bodyRegionsLabel ? ` (${r.bodyRegionsLabel})` : ''}.
            {multi ? ' Any of these offices can see you; call whichever suits you best.' : ''}
          </p>
          {multi ? (
            <section className={styles.block}>
              <h2 className={styles.h2}>Your options</h2>
              {openOptions.map((o) => {
                const addr = [o.practice.address, [o.practice.city, o.practice.state].filter(Boolean).join(', '), o.practice.zip].filter(Boolean).join(' · ');
                return (
                  <div key={o.id} className={styles.kv}>
                    <span>{o.position}.</span>
                    <span>
                      <span className={styles.strong}>{o.practice.name}</span>
                      {addr ? ` · ${addr}` : ''}
                      {o.practice.phone ? ` · ${o.practice.phone}` : ''}
                      {o.preferredProviderName ? ` · Ask for ${o.preferredProviderName}` : ''}
                    </span>
                  </div>
                );
              })}
              <p className={styles.muted}>Please call one office to schedule, and bring this sheet with you.</p>
            </section>
          ) : (
          <section className={styles.block}>
            <h2 className={styles.h2}>Where to go</h2>
            <div className={styles.kv}>
              <span>Office</span>
              <span>{rec?.name ?? '—'}</span>
              {recAddress && (
                <>
                  <span>Address</span>
                  <span>{recAddress}</span>
                </>
              )}
              {rec?.phone && (
                <>
                  <span>Phone</span>
                  <span>{rec.phone}</span>
                </>
              )}
              {preferredName && (
                <>
                  <span>Ask for</span>
                  <span>{preferredName}</span>
                </>
              )}
            </div>
            <p className={styles.muted}>Please call the office to schedule, and bring this sheet with you.</p>
          </section>
          )}
          {r.patientLinkPreview && (
            <section className={styles.qrRow}>
              <QRCode value={r.patientLinkPreview} size={96} />
              <div>
                <div className={styles.h2}>Your referral online</div>
                <div className={styles.muted}>Scan to see these details on your phone.</div>
                <div className={styles.url}>{r.patientLinkPreview}</div>
              </div>
            </section>
          )}
          <footer className={styles.foot}>
            Referral sent {r.sentAt ? formatDate(r.sentAt, 'MMMM d, yyyy') : ''}
            {r.senderPhone ? ` · Questions? Call ${r.senderPracticeName} at ${r.senderPhone}` : ''}
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <button type="button" onClick={() => window.print()} className={styles.printBtn}>
          Print
        </button>
      </div>
      <div className={styles.sheet}>
        <header className={styles.head}>
          <div>
            <div className={styles.practice}>{r.senderPracticeName}</div>
            <div className={styles.muted}>
              {[r.senderLocationName !== r.senderPracticeName ? r.senderLocationName : null, r.senderPhone].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div>
            <div className={styles.docTitle}>Patient referral</div>
            <div className={styles.muted}>{r.sentAt ? formatDate(r.sentAt, 'MMMM d, yyyy') : 'Draft'}</div>
          </div>
        </header>

        <section className={styles.toFrom}>
          <div>
            <div className={styles.label}>To</div>
            <div className={styles.strong}>{rec?.name ?? '—'}</div>
            {rec?.specialtyLabel && <div className={styles.muted}>{rec.specialtyLabel}</div>}
            {recAddress && <div className={styles.muted}>{recAddress}</div>}
            {rec?.phone && <div className={styles.muted}>{rec.phone}</div>}
            {r.preferredProviderName && <div className={styles.muted}>Attn: {r.preferredProviderName}</div>}
          </div>
          <div>
            <div className={styles.label}>From</div>
            <div className={styles.strong}>{r.referringDoctorName ?? r.senderPracticeName}</div>
            <div className={styles.muted}>{r.senderPracticeName}</div>
            {r.senderPhone && <div className={styles.muted}>{r.senderPhone}</div>}
          </div>
        </section>

        <section className={styles.grid}>
          <div>
            <h2 className={styles.h2}>Patient</h2>
            <div className={styles.kv}>
              <span>Name</span>
              <span>
                {r.patientLastName}, {r.patientFirstName}
              </span>
              <span>Phone</span>
              <span>{r.patientPhone ?? '—'}</span>
              {r.patientDob && (
                <>
                  <span>Date of birth</span>
                  <span>{formatDate(r.patientDob)}</span>
                </>
              )}
              {r.patientDetails?.insurance && (
                <>
                  <span>Insurance</span>
                  <span>
                    {r.patientDetails.insurance}
                    {r.patientDetails.memberId ? ` · ${r.patientDetails.memberId}` : ''}
                  </span>
                </>
              )}
              {r.patientDetails?.preferredLanguage && (
                <>
                  <span>Language</span>
                  <span>{r.patientDetails.preferredLanguage}</span>
                </>
              )}
            </div>

            <h2 className={styles.h2}>Reason for referral</h2>
            <p className={styles.text}>
              {r.reasons.join(', ') || '—'}
              {r.urgency !== 'routine' ? ` — ${r.urgency === 'urgent' ? 'URGENT' : 'Soon'}` : ''}
            </p>

            {r.comment && (
              <>
                <h2 className={styles.h2}>Comment</h2>
                <p className={styles.text}>{r.comment}</p>
              </>
            )}

            {r.patientDetails?.notes && (
              <>
                <h2 className={styles.h2}>Patient notes</h2>
                <p className={styles.text}>{r.patientDetails.notes}</p>
              </>
            )}
          </div>
          <div className={styles.chartCol}>
            <h2 className={styles.h2}>{r.vertical === 'dental' ? 'Teeth' : 'Affected area'}</h2>
            {r.vertical === 'dental' ? (
              <>
                <ToothChart teeth={t.teeth} selected={r.teeth} readOnly numbering={t.settings.toothNumbering} compact />
                <p className={styles.text}>{r.teethLabel || 'None noted'}</p>
              </>
            ) : (
              <p className={styles.text}>{r.bodyRegionsLabel || 'None noted'}</p>
            )}
          </div>
        </section>

        {r.attachments.length > 0 && (
          <section className={styles.block}>
            <h2 className={styles.h2}>Files on the digital referral</h2>
            <p className={styles.text}>
              {r.attachments.map((a) => `${a.label ?? 'File'}: ${a.originalFilename}`).join(' · ')}
            </p>
          </section>
        )}

        {officeLink && (
          <section className={styles.qrRow}>
            <QRCode value={officeLink} size={112} />
            <div>
              <div className={styles.h2}>Open the full referral online</div>
              <div className={styles.text}>
                Scan to view this referral with its X-rays and documents, mark the patient scheduled, and send a report back. No account
                needed. This link is private to your office.
              </div>
              <div className={styles.url}>{officeLink}</div>
            </div>
          </section>
        )}

        <section className={styles.sign}>
          <div>
            <div className={styles.sigLine} />
            <div className={styles.muted}>Referring {t.lexicon.providerNoun} signature</div>
          </div>
          <div>
            <div className={styles.sigLine} />
            <div className={styles.muted}>Date</div>
          </div>
        </section>

        <footer className={styles.foot}>Sent through PracticeNova · Referral {r.id}</footer>
      </div>
    </div>
  );
}
